import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import { schoolAdminNavItems } from '../../constants/navigation';

const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'password', label: 'Password' },
  { id: 'school', label: 'School Info' },
];

const SchoolAdminSettingsPage = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <DashboardLayout title="Settings" navItems={schoolAdminNavItems} userRole="school-admin" userName={currentUser?.full_name || 'Admin'}>
      <h2 className="text-3xl font-bold text-gray-900 mb-6">Settings</h2>

      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-4">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'profile' && <ProfileTab />}
      {activeTab === 'password' && <PasswordTab />}
      {activeTab === 'school' && <SchoolInfoTab />}
    </DashboardLayout>
  );
};

// ============================================================================
// PROFILE TAB
// ============================================================================
const ProfileTab = () => {
  const [, setProfile] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', job_title: '' });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await apiRequest('/api/school-admin/profile', 'GET');
        setProfile(data.profile);
        setSchool(data.school);
        setForm({
          full_name: data.profile?.full_name || '',
          email: data.profile?.email || '',
          phone: data.profile?.phone || '',
          job_title: data.profile?.job_title || '',
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await apiRequest('/api/school-admin/profile', 'PUT', form);
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h3>

      {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}
      {success && <Alert type="success" message={success} onDismiss={() => setSuccess(null)} />}

      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
          <input type="text" value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input type="text" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Optional" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
          <input type="text" value={form.job_title}
            onChange={(e) => setForm({ ...form, job_title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="e.g., Principal" />
        </div>

        {/* Read-only school info */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
          <input type="text" value={school?.school_name || ''} disabled
            className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500" />
        </div>

        <button type="submit" disabled={saving}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};

// ============================================================================
// PASSWORD TAB
// ============================================================================
const PasswordTab = () => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const getStrength = (password) => {
    if (!password) return { level: 0, label: '' };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

    if (score <= 2) return { level: 1, label: 'Weak', color: 'bg-red-500' };
    if (score <= 3) return { level: 2, label: 'Fair', color: 'bg-yellow-500' };
    if (score <= 4) return { level: 3, label: 'Good', color: 'bg-blue-500' };
    return { level: 4, label: 'Strong', color: 'bg-green-500' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (form.new_password !== form.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSaving(true);
      await apiRequest('/api/school-admin/password', 'PUT', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setSuccess('Password changed successfully');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const strength = getStrength(form.new_password);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>

      {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}
      {success && <Alert type="success" message={success} onDismiss={() => setSuccess(null)} />}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
          <input type="password" value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
          <input type="password" value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required />
          {form.new_password && (
            <div className="mt-2">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className={`h-1 flex-1 rounded-full ${i <= strength.level ? strength.color : 'bg-gray-200'}`} />
                ))}
              </div>
              <p className="text-xs text-gray-500">{strength.label} - Requires 8+ chars, uppercase, lowercase, number, special char</p>
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
          <input type="password" value={form.confirm_password}
            onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required />
          {form.confirm_password && form.new_password !== form.confirm_password && (
            <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
          )}
        </div>

        <button type="submit"
          disabled={saving || !form.current_password || !form.new_password || form.new_password !== form.confirm_password}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
          {saving ? 'Changing...' : 'Change Password'}
        </button>
      </form>
    </div>
  );
};

// ============================================================================
// SCHOOL INFO TAB
// ============================================================================
const SchoolInfoTab = () => {
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await apiRequest('/api/school-admin/profile', 'GET');
        setSchool(data.school);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  if (loading) return <LoadingSpinner />;

  if (error) {
    return <Alert type="error" message={error} />;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">School Information</h3>
      <p className="text-sm text-gray-500 mb-4">This information is managed by your district. Contact support to make changes.</p>

      <div className="space-y-4">
        <InfoRow label="School Name" value={school?.school_name} />
        <InfoRow label="District" value={school?.district_name} />
        <InfoRow label="City" value={school?.city} />
        <InfoRow label="State" value={school?.state} />
        <InfoRow label="Subscription Status" value={
          <span className={`capitalize font-medium ${school?.subscription_status === 'active' ? 'text-green-600' : 'text-gray-500'}`}>
            {school?.subscription_status || 'N/A'}
          </span>
        } />
        <InfoRow label="Subscription Expires" value={
          school?.subscription_end_date ? new Date(school.subscription_end_date).toLocaleDateString() : 'N/A'
        } />
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 text-sm">
      {value || 'N/A'}
    </div>
  </div>
);

// ============================================================================
// SHARED COMPONENTS
// ============================================================================
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-32">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
    <span className="ml-3 text-gray-600">Loading...</span>
  </div>
);

const Alert = ({ type, message, onDismiss }) => {
  const styles = type === 'error'
    ? 'bg-red-50 border-red-200 text-red-700'
    : 'bg-green-50 border-green-200 text-green-700';

  return (
    <div className={`border rounded-lg p-3 mb-4 flex justify-between items-center ${styles}`}>
      <span className="text-sm">{message}</span>
      {onDismiss && (
        <button onClick={onDismiss} className="text-sm opacity-60 hover:opacity-100 ml-2">&times;</button>
      )}
    </div>
  );
};

export default SchoolAdminSettingsPage;
